import React, { useState, useContext, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faTractor,
  faMinus,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { BasketContext } from "./BasketContext";
import axios from "axios";
import { useApiUrl } from "./ApiContext";

const productImages = importAll(require.context('./assets/images/products', false, /\.(png|jpe?g|webp|svg)$/));

const normalize = (s) =>
  s
    ? s
        .replace(/\s+/g, " ")
        .replace(/\u00A0/g, " ")
        .replace(/^\s+|\s+$/g, "")
        .toLowerCase()
        .replace(/\.jpeg$/, ".jpg")
        .replace(/\.jpe$/, ".jpg")
    : "";


function importAll(r) {
    let images = {};
    r.keys().forEach((key) => {
      const filename = key
        .replace("./", "")
        .toLowerCase()
        .replace(/\.jpeg$/, ".jpg")
        .replace(/\.jpg$/, ".jpg");
      images[filename] = r(key);
    });
    return images;
}


const stripExt = s => s?.replace(/\.[^/.]+$/, "").toLowerCase().trim();

const getGrassImage = (imageUrl) => {
    const normalizedImageUrl = normalize(imageUrl);
    const matchedKey = Object.keys(productImages).find(
        key => normalize(key) === normalizedImageUrl
    );
    return productImages[matchedKey];
};

const GrassFind = () => {
  const apiUrl = useApiUrl();
  const { addToBasket } = useContext(BasketContext);
  const [cuttingType, setCuttingType] = useState(
    localStorage.getItem("cuttingType") || ""
  );
  const [dairy, setDairy] = useState(localStorage.getItem("dairy") || "");
  const [beef, setBeef] = useState(localStorage.getItem("beef") || "");
  const [lamb, setLamb] = useState(localStorage.getItem("lamb") || "");
  const [horse, setHorse] = useState(localStorage.getItem("horse") || "");
  const [clover, setClover] = useState(localStorage.getItem("clover") || "");
  const [organic, setOrganic] = useState(localStorage.getItem("organic") || "");
  const [term, setTerm] = useState(localStorage.getItem("term") || "");
  const [sfi, setSfi] = useState(localStorage.getItem("sfi") || "");
  const [filteredGrasses, setFilteredGrasses] = useState(
    JSON.parse(localStorage.getItem("filteredGrasses")) || []
  );
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showResults, setShowResults] = useState(
    localStorage.getItem("showResults") === "true" || false
  );
  const [allGrasses, setAllGrasses] = useState([]);
  const [quantities, setQuantities] = useState(
    JSON.parse(localStorage.getItem("quantities")) || {}
  );

  const saveStateToLocalStorage = () => {
    localStorage.setItem("cuttingType", cuttingType);
    localStorage.setItem("dairy", dairy);
    localStorage.setItem("beef", beef);
    localStorage.setItem("lamb", lamb);
    localStorage.setItem("horse", horse);
    localStorage.setItem("clover", clover);
    localStorage.setItem("organic", organic);
    localStorage.setItem("term", term);
    localStorage.setItem("sfi", sfi);
    localStorage.setItem("filteredGrasses", JSON.stringify(filteredGrasses));
    localStorage.setItem("showResults", showResults);
    localStorage.setItem("quantities", JSON.stringify(quantities));
  };

  useEffect(() => {
    saveStateToLocalStorage();
  }, [
    cuttingType,
    dairy,
    beef,
    lamb,
    horse,
    clover,
    organic,
    term,
    sfi,
    filteredGrasses,
    showResults,
    quantities,
  ]);

  useEffect(() => {
    const fetchGrassVarieties = async () => {
      try {
        const response = await axios.get(`${apiUrl}/grass`);
        setAllGrasses(response.data);
      } catch (error) {
        console.error("Error fetching grass data:", error);
      }
    };
    fetchGrassVarieties();
  }, []);

  const possibleMatches = allGrasses.filter(
    (grass) =>
      (cuttingType ? grass.cutting_grazing === cuttingType : true) &&
      (dairy ? (dairy === "YES" ? grass.dairy : !grass.dairy) : true) &&
      (beef ? (beef === "YES" ? grass.beef : !grass.beef) : true) &&
      (lamb ? (lamb === "YES" ? grass.lamb : !grass.lamb) : true) &&
      (horse ? (horse === "YES" ? grass.horse : !grass.horse) : true) &&
      (clover ? grass.clover === clover : true) &&
      (organic
        ? grass.organic.toLowerCase() === organic.toLowerCase()
        : true) &&
      (term ? grass.term === term : true) &&
      (sfi ? (sfi === "YES" ? grass.SFI !== null : grass.SFI === null) : true)
  );

  const isAnyFilterSelected = () => {
    return (
      cuttingType ||
      dairy ||
      beef ||
      lamb ||
      horse ||
      clover ||
      organic ||
      term ||
      sfi
    );
  };

  const handleFindGrass = () => {
    setFilteredGrasses(possibleMatches);
    setShowResults(true);
  };

  const handleReset = () => {
    setCuttingType("");
    setDairy("");
    setBeef("");
    setLamb("");
    setHorse("");
    setClover("");
    setOrganic("");
    setTerm("");
    setSfi("");
    setFilteredGrasses([]);
    setShowResults(false);
    setQuantities({});
    localStorage.clear();
  };

  const handleProductClick = (grass) => {
    setSelectedProduct(grass);
  };

  const handleAddToBasket = (product) => {
    const quantity = quantities[product.name] || 1;
    addToBasket({
      ...product,
      quantity,
      kitDetails: {
        KitVariety: product.KitVariety,
        KitComGr: product.KitComGr,
        KitComm: product.KitComm,
      },
    });
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [product.name]: 1,
    }));
    setSelectedProduct(null);
  };

  const handleQuantityChange = (e, productName) => {
    const value = Math.max(1, Number(e.target.value));
    setQuantities((prevQuantities) => ({
      ...prevQuantities,
      [productName]: value,
    }));
  };

  return (
    <div className="grassfind-container">
      <div className="grass-finder">
        <h2>Grass Finder</h2>
        <div className="filter-options">
          <div className="filter-selects">
            <select
              value={cuttingType}
              onChange={(e) => setCuttingType(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Cutting/Grazing Type</option>
              <option value="CUTTING">Cutting</option>
              <option value="GRAZING">Grazing</option>
              <option value="DUAL PURPOSE">Dual Purpose</option>
              <option value="SPECIALIST GRAZING">Specialist Grazing</option>
            </select>

            <select
              value={dairy}
              onChange={(e) => setDairy(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Dairy Suitability</option>
              <option value="YES">Yes</option>
              <option value="NO">No</option>
            </select>

            <select
              value={beef}
              onChange={(e) => setBeef(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Beef Suitability</option>
              <option value="YES">Yes</option>
              <option value="NO">No</option>
            </select>

            <select
              value={lamb}
              onChange={(e) => setLamb(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Lamb Suitability</option>
              <option value="YES">Yes</option>
              <option value="NO">No</option>
            </select>

            <select
              value={horse}
              onChange={(e) => setHorse(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Horse Suitability</option>
              <option value="YES">Yes</option>
              <option value="NO">No</option>
            </select>

            <select
              value={clover}
              onChange={(e) => setClover(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Clover Type</option>
              <option value="RED">Red</option>
              <option value="WHITE">White</option>
              <option value="BOTH">Both</option>
              <option value="NIL">No Clover</option>
            </select>

            <select
              value={organic}
              onChange={(e) => setOrganic(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Organic Status</option>
              <option value="Organic">Organic</option>
              <option value="Non Organic">Non-Organic</option>
            </select>

            <select
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              disabled={showResults}
            >
              <option value="">Select Term</option>
              <option value="2 YEAR">2 Year</option>
              <option value="4 YEAR">4 Year</option>
              <option value="6 YEAR">6 Year</option>
              <option value="LONG TERM">Long Term</option>
            </select>

            <select
              value={sfi}
              onChange={(e) => setSfi(e.target.value)}
              disabled={showResults}
            >
              <option value="">SFI Included?</option>
              <option value="YES">Yes</option>
              <option value="NO">No</option>
            </select>
          </div>

          <div>
            {isAnyFilterSelected() && (
              <p>Possible grass matches: {possibleMatches.length}</p>
            )}
            {!showResults && (
              <button
                onClick={handleFindGrass}
                disabled={!isAnyFilterSelected() || possibleMatches < 1}
              >
                Find My Grass
              </button>
            )}
            {showResults && <button onClick={handleReset}>Start Again</button>}
          </div>
        </div>

        <div className="results">
          {showResults &&
            filteredGrasses.length > 0 &&
            filteredGrasses.map((grass, index) => (
              <div
                key={index}
                className="grass-card"
                onClick={() => handleProductClick(grass)}
              >
                <img src={getGrassImage(grass.imageUrl)} alt={grass.name} />
                <h3>{grass.name}</h3>
                <p
                  dangerouslySetInnerHTML={{
                    __html:
                      grass.description.length > 100
                        ? `${grass.description
                            .substring(0, 100)
                            .replace(/\\/g, "<br />")}...`
                        : grass.description.replace(/\\/g, "<br />"),
                  }}
                ></p>
                <div
                  style={{
                    textAlign: "center",
                    color: "blue",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                >
                  Click me to read more
                </div>
              </div>
            ))}
        </div>

        {selectedProduct && (
          <div className="grassfind-modal">
            <div className="grassfind-modal-content">
              <button
                className="close-modal"
                onClick={() => setSelectedProduct(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <h3>{selectedProduct.name}</h3>
              <p
                dangerouslySetInnerHTML={{
                  __html: selectedProduct.description.replace(/\\/g, "<br />"),
                }}
              ></p>

              <div className="shop-buttons-on-modal">
                <div className="quantity-controls">
                  <button
                    className="quantity-button"
                    onClick={() =>
                      setQuantities((prevQuantities) => ({
                        ...prevQuantities,
                        [selectedProduct.name]: Math.max(
                          1,
                          prevQuantities[selectedProduct.name] - 1
                        ),
                      }))
                    }
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={quantities[selectedProduct.name] || 1}
                    onChange={(e) =>
                      handleQuantityChange(e, selectedProduct.name)
                    }
                    className="quantity-input"
                  />
                  <button
                    className="quantity-button"
                    onClick={() =>
                      setQuantities((prevQuantities) => ({
                        ...prevQuantities,
                        [selectedProduct.name]:
                          (prevQuantities[selectedProduct.name] || 1) + 1,
                      }))
                    }
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>

                <button
                  onClick={() => {
                    handleAddToBasket(selectedProduct);
                    setSelectedProduct(null);
                  }}
                  className="add-to-basket-button"
                >
                  Add to <FontAwesomeIcon icon={faTractor} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrassFind;
